import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import React, { useEffect, useMemo, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { typography } from '../../../styles/typography.js';
import Card from '../../ui/card.js';
import { inventoryDashboardApi } from './Inventory_API.js';

ChartJS.register(ArcElement, Tooltip, Legend);

const TypeBreakdown = () => {
  const [typeBreakdownData, setTypeBreakdownData] = useState([]);
  const [isTypeLoading, setIsTypeLoading] = useState(true);
  const [typeError, setTypeError] = useState(null);

  const typeColorPalette = [
    '#9333EA',
    '#14B8A6',
    '#2563EB',
    '#F59E0B',
    '#EF4444',
    '#0EA5E9',
    '#10B981',
    '#F472B6',
  ];

  const totalTypeCount = useMemo(
    () =>
      typeBreakdownData.reduce(
        (sum, item) => sum + (Number(item.count) || 0),
        0
      ),
    [typeBreakdownData]
  );

  const chartData = useMemo(() => {
    if (!typeBreakdownData.length) {
      return {
        labels: [],
        datasets: [],
      };
    }

    return {
      labels: typeBreakdownData.map(item => item.name),
      datasets: [
        {
          data: typeBreakdownData.map(item => item.count),
          backgroundColor: typeBreakdownData.map(item => item.color),
          borderColor: '#ffffff',
          borderWidth: 2,
          hoverOffset: 6,
        },
      ],
    };
  }, [typeBreakdownData]);

  const chartOptions = useMemo(() => {
    return {
      cutout: '55%',
      plugins: {
        tooltip: {
          callbacks: {
            label: context => {
              const value = context.parsed ?? 0;
              const percentage =
                totalTypeCount > 0
                  ? ((value / totalTypeCount) * 100).toFixed(1)
                  : '0.0';
              const formattedValue = Number(value).toLocaleString();
              return `${context.label}: ${formattedValue} (${percentage}%)`;
            },
          },
        },
        legend: {
          display: false,
        },
      },
      responsive: true,
      maintainAspectRatio: false,
    };
  }, [totalTypeCount]);

  const fetchTypeBreakdownData = async () => {
    setIsTypeLoading(true);
    setTypeError(null);
    try {
      const response = await inventoryDashboardApi.getInventoryStatus();

      if (response?.success && response.data) {
        const summary = response.data.summary || response.data.location_summary;
        const typeBreakdown = summary?.type_breakdown || {};

        const parsedBreakdown = Object.entries(typeBreakdown).map(
          ([name, value], index) => ({
            name,
            count: Number(value) || 0,
            color: typeColorPalette[index % typeColorPalette.length],
          })
        );

        if (parsedBreakdown.length === 0) {
          setTypeError('Type breakdown data not available.');
        }

        setTypeBreakdownData(parsedBreakdown);
      } else {
        setTypeError(
          response?.message ||
            response?.error ||
            'Failed to fetch type breakdown data.'
        );
      }
    } catch (error) {
      setTypeError(
        error?.message || 'Unexpected error while fetching type breakdown data.'
      );
    } finally {
      setIsTypeLoading(false);
    }
  };

  useEffect(() => {
    fetchTypeBreakdownData();
  }, []);

  return (
    <div className="flex flex-col gap-4 flex-1">
      <h2
        className={typography.h3}
        style={{ color: '#000000', marginBottom: '0.5rem' }}
      >
        Type Breakdown
      </h2>

      <Card.Gradient className="p-6">
        {isTypeLoading ? (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <div className="h-10 w-10 border-4 border-t-transparent border-blue-500 rounded-full animate-spin" />
            <p className={typography.p} style={{ color: '#333333' }}>
              Loading type breakdown...
            </p>
          </div>
        ) : typeError ? (
          <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
            <p className={typography.p} style={{ color: '#DC2626' }}>
              {typeError}
            </p>
            <button
              type="button"
              onClick={fetchTypeBreakdownData}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm"
            >
              Retry
            </button>
          </div>
        ) : !typeBreakdownData.length ? (
          <div className="py-8 text-center">
            <p className={typography.p} style={{ color: '#333333' }}>
              No type breakdown data available.
            </p>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6 items-center">
            <div className="w-full lg:w-[215px] h-[215px] relative">
              <Doughnut data={chartData} options={chartOptions} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1 w-full">
              {typeBreakdownData.map(item => {
                const percentage =
                  totalTypeCount > 0
                    ? ((item.count / totalTypeCount) * 100).toFixed(1)
                    : '0.0';
                return (
                  <div key={item.name} className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: item.color }}
                    />
                    <div className="flex flex-col gap-0.5">
                      <p
                        className={typography.p}
                        style={{ color: '#000000', fontSize: '0.875rem' }}
                      >
                        {item.name}
                      </p>
                      <p
                        className={typography.p}
                        style={{ color: '#666666', fontSize: '0.75rem' }}
                      >
                        {item.count.toLocaleString()} ({percentage}%)
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card.Gradient>
    </div>
  );
};

export default TypeBreakdown;
